import { FC, useEffect, useMemo, useState } from 'react';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
    deleteDemoCredential,
    DemoCredentialListItem,
    demoCredentialListSelector,
    getDemoCredentials,
    setToastMessage,
    useAppDispatch,
} from '../../state';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export const DemoCredentialsList: FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const auth = useAuth();
    const jwtToken = auth.user?.access_token;
    const { list, currentPage, pageSize, totalElements } = useSelector(demoCredentialListSelector);

    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(DEFAULT_PAGE_SIZE);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [query]);

    useEffect(() => {
        setPage(0);
    }, [debouncedQuery]);

    useEffect(() => {
        if (!jwtToken) return;
        dispatch(getDemoCredentials({ jwtToken, query: debouncedQuery || undefined, page, size }));
    }, [jwtToken, debouncedQuery, page, size]);

    const credentialJsonBody = (row: { credentialJson: string }) => {
        const full = row.credentialJson;
        const preview = full.length > 30 ? `${full.slice(0, 30)}…` : full;
        return (
            <span
                title={full}
                style={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.85rem', fontFamily: 'monospace' }}
            >
                {preview}
            </span>
        );
    };

    const confirmDelete = (row: DemoCredentialListItem) => {
        confirmDialog({
            header: 'Delete demo credential',
            message: `Are you sure you want to delete demo credential "${row.templateId}" for customer ${row.customerId}? This action cannot be undone.`,
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: () => {
                if (!jwtToken) return;
                dispatch(deleteDemoCredential({ jwtToken, id: row.id })).then(response => {
                    if (response.type.includes('fulfilled')) {
                        dispatch(setToastMessage({ message: 'Demo credential deleted' }));
                        dispatch(getDemoCredentials({ jwtToken, query: debouncedQuery || undefined, page, size }));
                    }
                });
            },
        });
    };

    const deleteBody = (row: DemoCredentialListItem) => (
        <Button
            icon="pi pi-trash"
            className="p-button-danger p-button-text"
            aria-label={`Delete demo credential ${row.templateId}`}
            onClick={() => confirmDelete(row)}
        />
    );

    const onPage = (event: DataTablePageEvent) => {
        setPage(event.page ?? 0);
        if (event.rows && event.rows !== size) setSize(event.rows);
    };

    const first = useMemo(() => currentPage * pageSize, [currentPage, pageSize]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                <InputText
                    placeholder="Search customer, template, or credential JSON…"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    style={{ maxWidth: '24rem', flex: 1 }}
                />
                <Button
                    icon="pi pi-plus"
                    label="Create demo credential"
                    onClick={() => navigate('/demo-credentials')}
                />
            </div>
            <DataTable
                value={list}
                tableStyle={{ minWidth: '50rem' }}
                lazy
                paginator
                first={first}
                rows={pageSize || size}
                totalRecords={totalElements}
                onPage={onPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                sortField="tenantKey"
                sortOrder={1}
            >
                <Column field="tenantKey" header="Tenant" sortable />
                <Column field="customerId" header="Customer ID" sortable />
                <Column field="templateId" header="Template ID" sortable />
                <Column header="Credential JSON" body={credentialJsonBody} />
                <Column header="" body={deleteBody} style={{ width: '4rem' }} />
            </DataTable>
        </div>
    );
};
